import { AppHeader } from "./AppHeader";
import { Sidebar, SidebarProvider, useSidebar } from "./Sidebar";
import { cn } from "@/lib/utils";
import AiChatPopup from "@/components/AiChatPopup";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useLocation } from "wouter";
import { useEffect, useRef } from "react";

interface LayoutProps {
  children: React.ReactNode;
}

function LayoutContent({ children }: LayoutProps) {
  const { isCollapsed, setCollapsed } = useSidebar();
  const { user } = useAuth();
  const { currentRole } = useRole();
  const [pathname] = useLocation();
  const isImpersonating = !!user?.isImpersonating;

  // Auto-track route changes for feature analytics across every portal
  useAnalytics();

  // The quoting wizard's cards run wide (Step 5 especially), so default the
  // nav to collapsed there to give it the room. Only on ENTERING the route —
  // tracked per visit via a ref — so a broker who manually re-expands it
  // isn't fought back into collapsed on every render while still on the page.
  const collapsedForRouteRef = useRef<string | null>(null);
  useEffect(() => {
    const inQuotingWizard = pathname.includes("/quoting");
    if (inQuotingWizard && collapsedForRouteRef.current !== pathname) {
      collapsedForRouteRef.current = pathname;
      setCollapsed(true);
    } else if (!inQuotingWizard) {
      collapsedForRouteRef.current = null;
    }
  }, [pathname, setCollapsed]);

  // Key that changes when role OR pathname changes. React remounts the content
  // with a fresh fade-in animation, making role switches and nav feel smooth.
  const transitionKey = `${currentRole}:${pathname}`;

  // Prevent browser scroll restoration from jumping to focused elements (e.g., tabs) on reload
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // Ensure we start at the top on first render
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "auto";
      }
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-gray-50 dark:bg-gray-950"
      style={{ "--banner-offset": isImpersonating ? "2.25rem" : "0px" } as React.CSSProperties}
    >
      <ImpersonationBanner />
      <AppHeader />
      <Sidebar />
      <main
        className={cn(
          "min-h-screen transition-all duration-300",
          isCollapsed ? "ml-16" : "ml-64"
        )}
        style={{ paddingTop: isImpersonating ? "calc(4rem + 2.25rem)" : "4rem" }}
      >
        <div key={transitionKey} className="p-6 animate-in fade-in duration-200">
          {children}
        </div>
      </main>
      <AiChatPopup />
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  return (
    <SidebarProvider>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
}
